import type { FormValueEventInstance, FormValues, FormValuesEvent } from "@/widgets/classic-form/form";
import { EVENT_DEFAULT_INSTANCE } from "@/widgets/classic-form/form";
import { getEventModelName } from "@/entities/data-model";
import type { EventInstance } from "@/entities/data-model";
import type { EventFieldsGroup, EventType, FieldRelation } from "@/entities/form-metadata";
import { EventAssertion } from "@/entities/form-metadata";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";

const mapEventInstanceWithExactness = ({
    instance,
    isYearExactnessEnabled,
    isPlaceExactnessEnabled,
    matchAllTermsExactly,
}: {
    instance: FormValueEventInstance;
    isYearExactnessEnabled: boolean;
    isPlaceExactnessEnabled: boolean;
    matchAllTermsExactly: boolean;
}): FormValueEventInstance => {
    const copy = { ...instance };
    if (isYearExactnessEnabled && matchAllTermsExactly) {
        copy.dateProximity = {
            year: 0,
            day: 0,
            month: 0,
        };
    }
    if (isPlaceExactnessEnabled && matchAllTermsExactly) {
        copy.locationExactness = LOCATION_EXACTNESS_SCOPE.ThisPlace;
    }
    return copy;
};
export type EventFormInstance = {
    type: EventType;
    relation: FieldRelation;
    instances: Array<EventInstance>;
};

function toPascalCase(str: string) {
    return str
        .replace(/[_\- ]+/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join("");
}

const getDataModelLikeKey = ({ eventType, relation }: { eventType: EventType; relation: FieldRelation }) =>
    toPascalCase(`${relation} ${eventType ?? ""}`);

const PREDEFINED_EVENT_TYPES: (EventType | null)[] = [
    "Birth",
    "Death",
    "Burial",
    "Census",
    "Marriage",
    "Divorce",
    "Military",
    "Residence",
    "Other",
    null,
];

interface PopulateInstancesProps {
    formValuesEventsData: FormValues["events"];
    eventKey: string;
    dataModelEvent?: FormValuesEvent;
    prefilled?: boolean;
    isYearExactnessEnabled?: boolean;
    isPlaceExactnessEnabled?: boolean;
    matchAllTermsExactly: boolean;
}

const populateInstances = ({
    formValuesEventsData,
    eventKey,
    dataModelEvent,
    prefilled,
    matchAllTermsExactly,
    isYearExactnessEnabled = true,
    isPlaceExactnessEnabled = true,
}: PopulateInstancesProps) => {
    if (formValuesEventsData[eventKey]?.instances.length) return;
    if (dataModelEvent && dataModelEvent.instances.length) {
        const mappedInstances = dataModelEvent.instances.map((instance) =>
            mapEventInstanceWithExactness({
                instance,
                isYearExactnessEnabled,
                isPlaceExactnessEnabled,
                matchAllTermsExactly,
            }),
        );
        formValuesEventsData[eventKey]?.instances.push(...mappedInstances);
    } else if (prefilled && formValuesEventsData[eventKey]) {
        const mappedInstance = mapEventInstanceWithExactness({
            instance: EVENT_DEFAULT_INSTANCE,
            isYearExactnessEnabled,
            isPlaceExactnessEnabled,
            matchAllTermsExactly,
        });
        formValuesEventsData[eventKey].instances.push(mappedInstance);
    }
};

export const populateEventFields = ({
    fieldGroup,
    dataModel,
    formValuesEventsData,
    isEditForm,
    matchAllTermsExactly,
}: {
    fieldGroup: EventFieldsGroup;
    dataModel: FormValues;
    formValuesEventsData: FormValues["events"];
    isEditForm?: boolean;
    matchAllTermsExactly: boolean;
}) => {
    const { events: eventsFromDataModel } = dataModel;
    const { fieldsetDisplayStrategy } = fieldGroup;
    const prefilled = fieldsetDisplayStrategy !== "empty-dynamic";
    const removable = fieldsetDisplayStrategy !== "prefilled-static";
    if (!isEditForm && !prefilled && removable) return;

    fieldGroup.fieldsets.forEach((event) => {
        const { isYearExactnessEnabled, isPlaceExactnessEnabled } = event.fields.reduce(
            (acc, field) => {
                const {
                    assertion,
                    features: { showExactness },
                } = field;
                if (assertion === EventAssertion.Year) {
                    acc.isYearExactnessEnabled = showExactness ?? true;
                } else if (assertion === EventAssertion.Place) {
                    acc.isPlaceExactnessEnabled = showExactness ?? true;
                }
                return acc;
            },
            { isYearExactnessEnabled: true, isPlaceExactnessEnabled: true },
        );

        if (PREDEFINED_EVENT_TYPES.includes(event.eventType) && event.relation === "Self") {
            const eventKey = getDataModelLikeKey(event);
            const dataModelEvent = eventsFromDataModel[eventKey];

            if (formValuesEventsData[eventKey] && formValuesEventsData[eventKey].relation !== event.relation)
                formValuesEventsData[eventKey].relation = event.relation;
            if (formValuesEventsData[eventKey] && formValuesEventsData[eventKey].eventName !== event.eventType)
                formValuesEventsData[eventKey].eventName = event.eventType ?? "";

            populateInstances({
                formValuesEventsData,
                eventKey,
                dataModelEvent,
                prefilled: !removable || prefilled,
                isYearExactnessEnabled,
                isPlaceExactnessEnabled,
                matchAllTermsExactly,
            });
        } else {
            const eventKey = getEventModelName({
                eventType: event.eventType,
                relation: event.relation,
            });
            if (!eventKey) return;
            const dataModelEvent = eventsFromDataModel[eventKey];
            if (!formValuesEventsData[eventKey]) {
                formValuesEventsData[eventKey] = {
                    eventName: event.eventType ?? "",
                    instances: [],
                    relation: event.relation,
                };
            }
            populateInstances({
                formValuesEventsData,
                eventKey,
                dataModelEvent,
                prefilled: !removable || prefilled,
                isYearExactnessEnabled,
                isPlaceExactnessEnabled,
                matchAllTermsExactly,
            });
        }
    });
};
